import {
  CF_ORGANIZATION_KEY_NAME,
  getAccessToken,
  TOP_HOST,
  IMPERSONATE_KEY_NAME,
  isApiError,
} from "@/api/_client";
import {
  Credentials,
  logIn as userLogIn,
  logOut as userLogOut,
} from "@/api/auth";
import { Organization, useOrganizations } from "@/api/organizations";
import { useUser } from "@/api/user";
import { useToast } from "@/components/ui/use-toast";
import { deleteCookie, getCookie, setCookie } from "@/lib/cookies";
import { getSlug } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import React, { ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { AgentViewDescription } from "./agent-provider";
import { type ConfirmationProps, AppContext } from "./app-context";

const stopImpersonatingUser = () => {
  deleteCookie(IMPERSONATE_KEY_NAME);
  window.location.reload();
};

type Props = {
  children: ReactNode;
};

interface Clipboard {
  file: File;
  // entityId is the id of the project or portfolio the document belongs to
  // it is used to determine wether to display the move button
  entityId: string;
}

export function AppContextProvider({ children }: Props) {
  const navigate = useNavigate();
  const [globalError, setGlobalError] = React.useState<
    | {
        error: string;
        callback: () => unknown;
      }
    | undefined
  >();

  React.useEffect(() => {
    globalError?.callback();
  }, [globalError]);

  const [hideSidebar, setHideSidebar] = React.useState(false);
  const [collapseSidebar, setCollapseSidebar] = React.useState(false);
  const [minimizedLeftPanel, setMinimizedLeftPanel] = React.useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = React.useState(false);
  const [showAbsoluteSpinner, setShowAbsoluteSpinner] = React.useState(false);
  const [clipboard, setClipboard] = React.useState<Clipboard | undefined>(
    undefined,
  );
  const [currentChatSessionId, setCurrentChatSessionId] =
    React.useState<string>();
  const [isAgentMinimized, setIsAgentMinimized] = React.useState(true);
  const [agentSeedMessage, setAgentSeedMessage] = React.useState<string>();
  const [agentContextOverride, setAgentContextOverride] =
    React.useState<AgentViewDescription>();
  const [embeddedChatActive, setEmbeddedChatActive] = React.useState(false);

  const openAgent = React.useCallback(
    (seedMessage?: string, contextOverride?: AgentViewDescription) => {
      if (seedMessage) setAgentSeedMessage(seedMessage);
      if (contextOverride) setAgentContextOverride(contextOverride);
      setIsAgentMinimized(false);
    },
    [],
  );

  const clearAgentSeedMessage = React.useCallback(() => {
    setAgentSeedMessage(undefined);
  }, []);

  const clearAgentContextOverride = React.useCallback(() => {
    setAgentContextOverride(undefined);
  }, []);

  const [initialized, setInitialized] = React.useState(false);
  const [actingOrganization, setActingOrganization] =
    React.useState<Organization | null>();

  const organizations = useOrganizations();
  const queryClient = useQueryClient();
  const [confirmationState, setConfirmationState] =
    React.useState<ConfirmationProps>({} as ConfirmationProps);

  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();

  const useConfirmation = () => {
    const open = (props: ConfirmationProps) => {
      setConfirmationState({ ...props, isOpen: true });
    };
    const close = () => {
      setConfirmationState((prev) => ({ ...prev, isOpen: false }));
    };
    return { ...confirmationState, open, close };
  };

  const impersonatingUser = !!getCookie(IMPERSONATE_KEY_NAME);
  const user = useUser();
  const confirmation = useConfirmation();

  React.useEffect(() => {
    if (!user.data) return;
    if (!organizations.data) return;
    if (
      organizations.data &&
      organizations.data.count === 0 &&
      window.location.host !== TOP_HOST
    ) {
      window.location.replace(`${window.location.protocol}//${TOP_HOST}`);
    }
    if (window.location.host === TOP_HOST) {
      setActingOrganization(null);
    } else {
      const slug = getSlug();
      const organization = organizations.data?.results?.find(
        (o) => o.slug === slug,
      );
      if (organization) {
        setActingOrganization(organization);
        setCookie(CF_ORGANIZATION_KEY_NAME, organization.id);
      } else {
        const newUrl = `${window.location.protocol}//${TOP_HOST}`;
        window.location.replace(newUrl);
      }
    }
  }, [organizations]);

  React.useEffect(() => {
    const emailChangeStatus = searchParams.get("email_change_success");
    const err = searchParams.get("err");
    if (err === "session_expired") {
      toast({
        title: "Session expired",
        description: "You were logged out because your session has expired",
      });
      setSearchParams();
    }
    if (emailChangeStatus === "true") {
      confirmation.open({
        title: "Email change successful",
        description:
          "Your email has been successfully updated. You will have to verify your new email to continue using your account.",
        hideCancel: true,
        confirmText: "Ok",
      });
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      setSearchParams();
    } else if (emailChangeStatus === "false") {
      confirmation.open({
        title: "Email change failed",
        description: "Your email could not be updated. Please try again.",
        hideCancel: true,
        confirmText: "Ok",
      });
      setSearchParams();
    }
    if (!getAccessToken()) {
      setInitialized(true);
      return;
    }
  }, []);

  React.useEffect(() => {
    // checks we have everything we need for initialization
    if (initialized) return;
    if (!organizations.data) return;
    if (!user.data) return;
    setInitialized(true);
  }, [user.data, organizations, initialized]);

  const logOut = async () => {
    queryClient.setQueryData(["currentUser"], null);
    await userLogOut();
    queryClient.resetQueries();
  };

  const logIn = async (credentials: Credentials) => {
    queryClient.resetQueries();
    const res = await userLogIn(credentials);
    if (isApiError(res)) throw res.error;
    if (!("mfa" in res.data)) {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      setInitialized(false);
    }
    return res;
  };

  const swapOrganization = (organization: Organization) => {
    queryClient.setQueryData(["organizations"], null);
    setInitialized(false);
    setActingOrganization(organization);
    setCookie(CF_ORGANIZATION_KEY_NAME, organization.id);
    queryClient.resetQueries();
    navigate("/");
  };

  const hasPermission = (permission: string) => {
    return actingOrganization?.permissions?.includes(permission) || false;
  };

  return (
    <AppContext.Provider
      value={{
        logIn,
        logOut,
        initialized,
        actingOrganization,
        collapseSidebar,
        setCollapseSidebar,
        minimizedLeftPanel,
        setMinimizedLeftPanel,
        useConfirmation,
        swapOrganization,
        setShowAbsoluteSpinner,
        showAbsoluteSpinner,
        clipboard,
        setClipboard,
        hasPermission,
        globalError,
        setGlobalError,
        showMobileSidebar,
        setShowMobileSidebar,
        hideSidebar,
        setHideSidebar,
        impersonatingUser,
        stopImpersonatingUser,
        setCurrentChatSessionId,
        currentChatSessionId,
        openAgent,
        isAgentMinimized,
        setIsAgentMinimized,
        agentSeedMessage,
        clearAgentSeedMessage,
        agentContextOverride,
        clearAgentContextOverride,
        embeddedChatActive,
        setEmbeddedChatActive,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}
